import React, { useEffect, useMemo } from "react";
import { ConnectedProps, connect } from "react-redux";
import { AppDispatch, RootState } from "store";
import { DetailPreliquidation } from "./DetailPreliquidation";
import {
  DetailPreliquidationContainerPropsType,
  DetailPreliquidationForm,
} from "./types";
import { preliquidationSelector, actions } from "reducers/preliquidation";
import { useHistory, useParams } from "react-router-dom";
import { NotificationStateType } from "reducers/types/notification";
import { actions as notificationActions } from "reducers/notification";
import { PagesPreliquidationTypes } from "../types";
import moment from "moment";
import {
  BULK_FLOW_ID,
  DATE_FORMATS,
  PRELIQUIDATION_STATUS_NAMES,
} from "utils/constants";
import { sessionSelector, actions as sessionActions } from "reducers/session";
import { DEFAULT_COUNTRY } from "hooks/useSession";
import "./i18n";
import { useTranslation } from "react-i18next";

export const DetailPreliquidationContainer = (
  props: DetailPreliquidationContainerPropsType & ConnectorProps
): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const params: { id?: string } = useParams();

  const handleClickBack = () => {
    props.setActualPage("preliquidation");
    history.replace(`/${props.countryCode}/presettlements`);
  };

  useEffect(() => {
    props.setDirty(false);

    if (params.id === BULK_FLOW_ID) {
      const { invoiceIds, currentInvoice } = props;
      const len = invoiceIds?.length;
      if (len > 0 && currentInvoice < len) {
        props.getDetailPreliquidation(invoiceIds[currentInvoice]);
      } else {
        handleClickBack();
      }
    } else {
      const id = Number(params.id);
      if (isNaN(id)) {
        handleClickBack();
      } else {
        props.getDetailPreliquidation(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues: DetailPreliquidationForm = useMemo(() => {
    const { preliquidation } = props;
    const { fiscalNumber: fn } = preliquidation;
    return {
      status: preliquidation?.status
        ? t(`${PRELIQUIDATION_STATUS_NAMES[preliquidation.status]}`)
        : "-",
      emisionDate: preliquidation.generatedAt
        ? moment(preliquidation.generatedAt).format(DATE_FORMATS.shortDate)
        : "-",
      companyName: preliquidation.companyName || "-",
      fiscalNumber: fn || "-",
      sapCode: preliquidation.sapCode || "-",
      region: preliquidation.region?.name || "-",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.preliquidation]);

  return (
    <DetailPreliquidation
      initialValues={initialValues}
      testId="detail-preliquidation"
      {...props}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  actualPage: preliquidationSelector(state).actualPage,
  preliquidation: preliquidationSelector(state).detailPreliquidations,
  countryCode: sessionSelector(state).countryCode || DEFAULT_COUNTRY,
  invoiceIds: preliquidationSelector(state).pendingApprovalPresettlements,
  currentInvoice: preliquidationSelector(state).currentInvoice,
  dirty: sessionSelector(state).dirtyForm,
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  setActualPage: (page: PagesPreliquidationTypes) => {
    dispatch(actions.setActualPage(page));
  },
  getDetailPreliquidation: (id: number) => {
    dispatch(actions.getDetailPreliquidationsRequest(id));
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  toggleModalVisibility: (isOpen: boolean) => {
    dispatch(actions.toggleModalVisibility(isOpen));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(DetailPreliquidationContainer);
